import React from 'react';
import { Helmet } from 'react-helmet';
import SEOHelmet from '../../components/SEOHelmet';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import PublicNavigation from '../../components/ui/PublicNavigation';
import TrackingForm from './components/TrackingForm';

import ContactSection from './components/ContactSection';
import HowItWorks from './components/HowItWorks';
import Icon from '../../components/AppIcon';

const HomeTracking = () => {
  const infoCards = [
  {
    icon: 'Clock',
    title: 'Suivi en temps réel',
    description: 'Visualisez chaque étape de votre envoi de Paris à Abidjan'
  },
  {
    icon: 'Shield',
    title: 'Sécurité garantie',
    description: 'Tous nos envois sont assurés et manipulés avec soin'
  },
  {
    icon: 'Truck',
    title: 'Livraison fiable',
    description: 'Délai moyen de 4 à 6 semaines avec livraison à domicile'
  }];


  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
      opacity: 1,
      y: 0,
      transition: {
        duration: 0.5
      }
    }
  };

  const structuredDataTracking = {
    "@context": "https://schema.org",
    "@graph": [
    {
      "@type": "WebPage",
      "name": "Suivi d\'envoi ADT Express",
      "description": "Suivez votre colis en temps réel de Paris à Abidjan",
      "url": "https://www.adtexpress.fr/suivi"
    },
    {
      "@type": "BreadcrumbList",
      "itemListElement": [
      {
        "@type": "ListItem",
        "position": 1,
        "name": "Accueil",
        "item": "https://www.adtexpress.fr"
      },
      {
        "@type": "ListItem",
        "position": 2,
        "name": "Suivi",
        "item": "https://www.adtexpress.fr/suivi"
      }]

    }]

  };

  return (
    <>
      <SEOHelmet
        title="Suivi Colis Côte d'Ivoire en Temps Réel | Tracking Barrique Abidjan - ADT Express"
        description="Suivez votre envoi vers la Côte d'Ivoire en temps réel. Tracking barrique Abidjan, suivi conteneur France CI, localisation colis Paris Abidjan. Visibilité complète de votre expédition."
        keywords={"suivi colis Côte d'Ivoire, tracking barrique Abidjan, suivi envoi Paris Abidjan, localisation conteneur France CI, suivi fret maritime Abidjan, envoyer barrique Côte d\u2019Ivoire, groupage maritime, tarif envoi Abidjan"}
        canonicalUrl="https://www.adtexpress.fr/suivi"
        structuredData={structuredDataTracking} />
      <Helmet>
        <title>Suivi d&apos;envoi - ADT Express | Suivez votre colis en temps réel</title>
        <meta
          name="description"
          content="Suivez votre envoi ADT Express en temps réel de Paris à Abidjan. Entrez votre référence pour connaître le statut de votre colis." />
        
      </Helmet>
      <div className="min-h-screen bg-white">
        <PublicNavigation />

        {/* Hero Section with Container Photo Background */}
        <section className="relative bg-gradient-to-br from-green-600 to-green-800 text-white py-20 px-4 overflow-hidden pt-44">
          <div className="absolute inset-0">
            <img
              src="https://img.rocket.new/generatedImages/rocket_gen_img_180d17e0c-1780788247338.png"
              alt="Conteneurs maritimes au port pour envoi vers Abidjan"
              className="w-full h-full object-cover opacity-40"
              loading="lazy" />
            
            <div className="absolute inset-0 bg-black opacity-60" />
          </div>
          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7 }}
              className="text-center mb-8">
              
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold text-white mb-4">
                Suivez votre envoi en temps réel
              </h1>
              <p className="text-lg sm:text-xl text-gray-200 max-w-3xl mx-auto">
                Entrez votre numéro de référence pour connaître le statut de votre colis
              </p>
            </motion.div>

            {/* Trust Badges */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, delay: 0.2 }}
              className="flex flex-wrap justify-center items-center gap-6 text-white text-sm">
              
              <div className="flex items-center space-x-2">
                <Icon name="Shield" size={20} color="white" />
                <span>Sécurisé & Assuré</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="Ship" size={20} color="white" />
                <span>2 départs/mois</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="CheckCircle" size={20} color="white" />
                <span>Service Certifié</span>
              </div>
              <div className="flex items-center space-x-2">
                <Icon name="MessageCircle" size={20} color="white" />
                <span>Support 7j/7</span>
              </div>
            </motion.div>
          </div>
        </section>

        <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Main Tracking Search Bar - Immediately Visible */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            className="-mt-16 relative z-10 mb-16">
            
            <TrackingForm />
          </motion.div>

          {/* How It Works - Simplified to 3 Steps */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.5 }}
            className="mb-16">
            
            <HowItWorks />
          </motion.div>

          {/* Info Cards - Compact Single Line */}
          <motion.div
            variants={containerVariants}
            initial="hidden"
            animate="visible"
            className="bg-gray-50 py-12 -mx-4 sm:-mx-6 lg:-mx-8 px-4 sm:px-6 lg:px-8 mb-16">
            
            <div className="max-w-7xl mx-auto">
              <div className="grid md:grid-cols-3 gap-6">
                {infoCards?.map((card, index) =>
                <motion.div key={index} variants={itemVariants}>
                    <div className="bg-white rounded-xl shadow-sm p-6 h-full hover:shadow-md transition-shadow">
                      <div className="flex items-start space-x-4">
                        <div className="flex-shrink-0 w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center">
                          <Icon name={card?.icon} size={24} className="text-green-700" />
                        </div>
                        <div>
                          <h3 className="text-lg font-bold text-gray-900 mb-1">
                            {card?.title}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {card?.description}
                          </p>
                        </div>
                      </div>
                    </div>
                  </motion.div>
                )}
              </div>
            </div>
          </motion.div>

          {/* Help Section - Simplified Green Banner */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.7, delay: 0.7 }}
            className="mb-16">
            
            <ContactSection />
          </motion.div>
        </main>

        {/* Footer - Complete 4 Column Footer */}
        <footer className="bg-green-900 text-white mt-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
            <div className="grid md:grid-cols-4 gap-8 mb-8">
              {/* Column 1: ADT Express */}
              <div>
                <h3 className="text-xl font-bold mb-4">ADT Express</h3>
                <p className="text-gray-300 text-sm leading-relaxed">
                  Votre partenaire de confiance pour l'expédition de colis entre la France et la Côte d'Ivoire depuis plus de 10 ans.
                </p>
              </div>

              {/* Column 2: Navigation */}
              <div>
                <h3 className="text-lg font-bold mb-4">Navigation</h3>
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link to="/" className="text-gray-300 hover:text-white transition-colors">
                      Accueil
                    </Link>
                  </li>
                  <li>
                    <Link to="/pricing" className="text-gray-300 hover:text-white transition-colors">
                      Tarifs
                    </Link>
                  </li>
                  <li>
                    <Link to="/tracking" className="text-gray-300 hover:text-white transition-colors">
                      Suivi
                    </Link>
                  </li>
                  <li>
                    <Link to="/blog" className="text-gray-300 hover:text-white transition-colors">
                      Blog
                    </Link>
                  </li>
                  <li>
                    <Link to="/shipping-information" className="text-gray-300 hover:text-white transition-colors">
                      Qui sommes-nous
                    </Link>
                  </li>
                </ul>
              </div>

              {/* Column 3: Services */}
              <div>
                <h3 className="text-lg font-bold mb-4">Services</h3>
                <ul className="space-y-2 text-sm">
                  <li>
                    <Link to="/client-login" className="text-gray-300 hover:text-white transition-colors">
                      Espace Client
                    </Link>
                  </li>
                  <li>
                    <Link to="/professional-services" className="text-gray-300 hover:text-white transition-colors">
                      Espace Professionnel
                    </Link>
                  </li>
                  <li>
                    <span className="text-gray-300">Barriques Noire & Bleue</span>
                  </li>
                  <li>
                    <span className="text-gray-300">Cartons Triple Cannelure</span>
                  </li>
                  <li>
                    <span className="text-gray-300">Livraison à domicile</span>
                  </li>
                </ul>
              </div>

              {/* Column 4: Contact */}
              <div>
                <h3 className="text-lg font-bold mb-4">Contact</h3>
                <ul className="space-y-3 text-sm">
                  <li className="flex items-start space-x-2">
                    <Icon name="MapPin" size={16} className="flex-shrink-0 mt-1" color="white" />
                    <span className="text-gray-300">23 rue de l'essonne 91000 evry</span>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="Phone" size={16} className="flex-shrink-0 mt-1" color="white" />
                    <a href="tel:0651988621" className="text-gray-300 hover:text-white transition-colors">
                      06 51 98 86 21
                    </a>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="MessageCircle" size={16} className="flex-shrink-0 mt-1" color="white" />
                    <a
                      href="https://wa.me/33651988621"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-gray-300 hover:text-white transition-colors">
                      
                      WhatsApp
                    </a>
                  </li>
                  <li className="flex items-start space-x-2">
                    <Icon name="Mail" size={16} className="flex-shrink-0 mt-1" color="white" />
                    <span className="text-gray-300">contact@adtexpress.fr</span>
                  </li>
                </ul>
              </div>
            </div>

            {/* Bottom Bar */}
            <div className="border-t border-green-800 pt-8">
              <div className="flex flex-col md:flex-row justify-between items-center space-y-4 md:space-y-0">
                <p className="text-sm text-gray-300">
                  © 2026 ADT Express — Tous droits réservés
                </p>
                <div className="flex items-center space-x-6 text-sm">
                  <Link to="/mentions-legales" className="text-gray-300 hover:text-white transition-colors">
                    Mentions légales
                  </Link>
                  <Link to="/cgv" className="text-gray-300 hover:text-white transition-colors">
                    CGV
                  </Link>
                </div>
              </div>
            </div>
          </div>
        </footer>
      </div>
    </>);







};

export default HomeTracking;